import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/** Log event types for Verisk SSO flow */
export type VeriskLogEvent =
  | 'SSO_DEEP_LINK_ISSUER_DETECTED'
  | 'SSO_LOGIN_API_REDIRECT'
  | 'SSO_TOKEN_VALIDATION_SUCCESS'
  | 'SSO_TOKEN_VALIDATION_FAILED'
  | 'SSO_NO_SESSION_REDIRECT_LOGIN'
  | 'SSO_LOGOUT'
  | 'SSO_GUARD_CHECK';

export interface VeriskLogPayload {
  event: VeriskLogEvent;
  timestamp: string;
  url: string;
  fullUrl?: string;
  identifier?: string;
  returnUrl?: string;
  route?: string;
  hasVeriskToken?: boolean;
  hasLocalSession?: boolean;
  source?: string;
  error?: string;
  [key: string]: unknown;
}

/**
 * General logging service - .NET-style API for Angular.
 * Use logInformation, logWarning, logError anywhere in the app.
 * Sends to backend api/log for AWS CloudWatch.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private readonly LOG_API_URL = `${environment.WebApiURL}api/log`;

  constructor(private http: HttpClient) {}

  /**
   * Log information - like _logger.LogInformation in .NET
   * @param message - Log message (supports {0}, {1} placeholders)
   * @param args - Optional args for placeholders or object for structured data
   */
  logInformation(message: string, ...args: unknown[]): void {
    this.log('info', message, args);
  }

  /**
   * Log warning - logs as info level
   */
  logWarning(message: string, ...args: unknown[]): void {
    this.log('info', message, args);
  }

  /**
   * Log error - logs as info level
   */
  logError(message: string, ...args: unknown[]): void {
    this.log('info', message, args);
  }

  private log(level: string, message: string, args: unknown[], category?: string): void {
    let formattedMessage = message;
    let data: Record<string, unknown> = { url: window.location.pathname + window.location.search, fullUrl: window.location.href };

    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null && !Array.isArray(args[0])) {
      data = { ...data, ...(args[0] as Record<string, unknown>) };
    } else {
      args.forEach((arg, i) => {
        formattedMessage = formattedMessage.replace(`{${i}}`, String(arg));
        data[`arg${i}`] = arg;
      });
    }

    // if (category) {
    //   data.category = category;
    // }

    const payload = {
      level,
      timestamp: new Date().toISOString(),
      message: formattedMessage,
      ...data
    };

    const logLine = category ? `[${category}] ${formattedMessage}` : formattedMessage;
    console.log(logLine, data);

    this.sendToBackend(payload);
  }

  private sendToBackend(payload: Record<string, unknown>): void {
    this.http.post(this.LOG_API_URL, payload, {
      headers: { 'Content-Type': 'application/json', 'IgnoreInterceptor': '' }
    }).subscribe({ error: () => {} });
  }

  /** Log with category - e.g. logInformation('User logged in', 'Auth', { userId: 1 }) */
  logWithCategory(message: string, category: string, ...args: unknown[]): void {
    this.log('info', message, args, category);
  }

  /** Log Verisk event with structured payload - all as info level */
  logVeriskEvent(payload: Partial<VeriskLogPayload> & { event: VeriskLogEvent }): void {
    const { event, ...rest } = payload;
    const fullPayload: VeriskLogPayload & { level: string } = {
      ...rest,
      event,
      level: 'info',
      timestamp: new Date().toISOString(),
      url: payload.url ?? window.location.pathname + window.location.search,
      fullUrl: payload.fullUrl ?? window.location.href
    };
    const message = `${event} - Url: ${fullPayload.url}`;
    console.log(`[VeriskSSO] ${message}`, fullPayload);
    this.sendToBackend(fullPayload as unknown as Record<string, unknown>);
  }

  logIdentifierDetected(url: string, identifier: string, returnUrl: string, source: string): void {
    this.logVeriskEvent({
      event: 'SSO_DEEP_LINK_ISSUER_DETECTED',
      url,
      fullUrl: window.location.origin + (url.startsWith('/') ? url : '/' + url),
      identifier,
      returnUrl,
      source
    });
  }

  logSsoLoginRedirect(url: string, identifier: string, returnUrl: string, apiUrl: string): void {
    this.logVeriskEvent({
      event: 'SSO_LOGIN_API_REDIRECT',
      url,
      identifier,
      returnUrl,
      source: 'VeriskAuthGuard',
      apiUrl
    });
  }

  logTokenValidation(success: boolean, url: string, hasToken: boolean, error?: string): void {
    this.logVeriskEvent({
      event: success ? 'SSO_TOKEN_VALIDATION_SUCCESS' : 'SSO_TOKEN_VALIDATION_FAILED',
      url,
      hasVeriskToken: hasToken,
      error: error
    });
  }

  logNoSessionRedirect(url: string, hasIdentifier: boolean): void {
    this.logVeriskEvent({
      event: 'SSO_NO_SESSION_REDIRECT_LOGIN',
      url,
      identifier: hasIdentifier ? 'present' : undefined
    });
  }

  logLogout(url: string): void {
    this.logVeriskEvent({ event: 'SSO_LOGOUT', url, source: 'LogoutComponent' });
  }

  logGuardCheck(url: string, hasVeriskToken: boolean, hasLocalSession: boolean, route: string): void {
    this.logVeriskEvent({
      event: 'SSO_GUARD_CHECK',
      url,
      route,
      hasVeriskToken,
      hasLocalSession
    });
  }
}